import React, { useState } from "react";
import { useQuery, useMutation, useQueryClient } from "@tanstack/react-query";
import {
  getRemoteConfigs,
  createRemoteConfig,
  updateRemoteConfig,
  deleteRemoteConfig,
} from "../../../data";
import { AdminLayout } from "../shared/layout";
import { Badge } from "@/components/ui/badge";
import { Button } from "@/components/ui/button";
import { Card, CardContent, CardHeader, CardTitle } from "@/components/ui/card";
import {
  Table,
  TableBody,
  TableCell,
  TableHead,
  TableHeader,
  TableRow,
} from "@/components/ui/table";
import {
  Dialog,
  DialogContent,
  DialogDescription,
  DialogFooter,
  DialogHeader,
  DialogTitle,
} from "@/components/ui/dialog";
import {
  Select,
  SelectContent,
  SelectItem,
  SelectTrigger,
  SelectValue,
} from "@/components/ui/select";
import { Input } from "@/components/ui/input";
import { Label } from "@/components/ui/label";
import { Textarea } from "@/components/ui/textarea";
import { toast } from "@/components/ui/use-toast";
import {
  Settings,
  Plus,
  Pencil,
  Trash2,
} from "lucide-react";
import {
  AlertDialog,
  AlertDialogAction,
  AlertDialogCancel,
  AlertDialogContent,
  AlertDialogDescription,
  AlertDialogFooter,
  AlertDialogHeader,
  AlertDialogTitle,
  AlertDialogTrigger,
} from "@/components/ui/alert-dialog";

export function RemoteConfigScreen() {
  const queryClient = useQueryClient();
  const [manageDialog, setManageDialog] = useState({ open: false, config: null });
  const [formData, setFormData] = useState({
    key: "",
    value: "",
    valueType: "string",
    description: "",
    platform: "all",
  });

  const { data, isLoading } = useQuery({
    queryKey: ["remote-configs"],
    queryFn: getRemoteConfigs,
  });

  const createMutation = useMutation({
    mutationFn: createRemoteConfig,
    onSuccess: () => {
      queryClient.invalidateQueries(["remote-configs"]);
      setManageDialog({ open: false, config: null });
      resetForm();
      toast({
        title: "Başarılı",
        description: "Config oluşturuldu",
        variant: "success",
      });
    },
    onError: (error) => {
      toast({
        title: "Hata",
        description: error.response?.data?.message || "İşlem başarısız",
        variant: "destructive",
      });
    },
  });

  const updateMutation = useMutation({
    mutationFn: ({ id, data }) => updateRemoteConfig(id, data),
    onSuccess: () => {
      queryClient.invalidateQueries(["remote-configs"]);
      setManageDialog({ open: false, config: null });
      resetForm();
      toast({
        title: "Başarılı",
        description: "Config güncellendi",
        variant: "success",
      });
    },
  });

  const deleteMutation = useMutation({
    mutationFn: deleteRemoteConfig,
    onSuccess: () => {
      queryClient.invalidateQueries(["remote-configs"]);
      toast({
        title: "Başarılı",
        description: "Config silindi",
        variant: "success",
      });
    },
  });

  const resetForm = () => {
    setFormData({
      key: "",
      value: "",
      valueType: "string",
      description: "",
      platform: "all",
    });
  };

  const handleCreate = () => {
    setManageDialog({ open: true, config: null });
    resetForm();
  };

  const handleEdit = (config) => {
    setManageDialog({ open: true, config });
    setFormData({
      key: config.key,
      value: config.value,
      valueType: config.valueType,
      description: config.description || "",
      platform: config.platform,
    });
  };

  const handleSubmit = () => {
    if (manageDialog.config) {
      updateMutation.mutate({
        id: manageDialog.config.id,
        data: {
          value: formData.value,
          valueType: formData.valueType,
          description: formData.description,
          platform: formData.platform,
        },
      });
    } else {
      createMutation.mutate(formData);
    }
  };

  const configs = data?.data?.data || [];

  const actions = [
    {
      name: "Yeni Config",
      icon: <Plus className="mr-2 h-4 w-4" />,
      onClick: handleCreate,
    },
  ];

  return (
    <AdminLayout
      title="Remote Config"
      description="Uygulama ayarlarını dinamik olarak yönetin"
      loading={isLoading}
      actions={actions}
    >
      <Card>
        <CardHeader>
          <CardTitle className="flex items-center">
            <Settings className="mr-2 h-5 w-5" />
            Configurations ({configs.length})
          </CardTitle>
        </CardHeader>
        <CardContent>
          {configs.length === 0 ? (
            <div className="text-center py-8 text-gray-500">
              <Settings className="mx-auto h-12 w-12 mb-4 text-gray-300" />
              <p>Henüz config bulunmuyor.</p>
              <Button onClick={handleCreate} className="mt-4">
                <Plus className="mr-2 h-4 w-4" />
                İlk Config'i Oluştur
              </Button>
            </div>
          ) : (
            <Table>
              <TableHeader>
                <TableRow>
                  <TableHead>Key</TableHead>
                  <TableHead>Value</TableHead>
                  <TableHead>Type</TableHead>
                  <TableHead>Platform</TableHead>
                  <TableHead>Açıklama</TableHead>
                  <TableHead>İşlemler</TableHead>
                </TableRow>
              </TableHeader>
              <TableBody>
                {configs.map((config) => (
                  <TableRow key={config.id}>
                    <TableCell>
                      <code className="text-sm bg-gray-100 px-2 py-1 rounded">
                        {config.key}
                      </code>
                    </TableCell>
                    <TableCell className="font-medium max-w-xs truncate">
                      {config.value}
                    </TableCell>
                    <TableCell>
                      <Badge variant="outline">{config.valueType}</Badge>
                    </TableCell>
                    <TableCell>
                      <Badge variant="outline">{config.platform.toUpperCase()}</Badge>
                    </TableCell>
                    <TableCell>
                      <div className="max-w-xs truncate text-sm text-gray-600">
                        {config.description || "—"}
                      </div>
                    </TableCell>
                    <TableCell>
                      <div className="flex space-x-2">
                        <Button
                          variant="ghost"
                          size="icon"
                          onClick={() => handleEdit(config)}
                        >
                          <Pencil className="h-4 w-4" />
                        </Button>
                        <AlertDialog>
                          <AlertDialogTrigger asChild>
                            <Button variant="ghost" size="icon">
                              <Trash2 className="h-4 w-4 text-red-500" />
                            </Button>
                          </AlertDialogTrigger>
                          <AlertDialogContent>
                            <AlertDialogHeader>
                              <AlertDialogTitle>Config'i Sil</AlertDialogTitle>
                              <AlertDialogDescription>
                                Bu config'i silmek istediğinizden emin misiniz?
                              </AlertDialogDescription>
                            </AlertDialogHeader>
                            <AlertDialogFooter>
                              <AlertDialogCancel>İptal</AlertDialogCancel>
                              <AlertDialogAction
                                onClick={() => deleteMutation.mutate(config.id)}
                              >
                                Sil
                              </AlertDialogAction>
                            </AlertDialogFooter>
                          </AlertDialogContent>
                        </AlertDialog>
                      </div>
                    </TableCell>
                  </TableRow>
                ))}
              </TableBody>
            </Table>
          )}
        </CardContent>
      </Card>

      {/* Create/Edit Dialog */}
      <Dialog
        open={manageDialog.open}
        onOpenChange={(open) => {
          setManageDialog({ open, config: manageDialog.config });
          if (!open) resetForm();
        }}
      >
        <DialogContent className="max-w-2xl">
          <DialogHeader>
            <DialogTitle>
              {manageDialog.config ? "Config Düzenle" : "Yeni Config"}
            </DialogTitle>
          </DialogHeader>

          <div className="space-y-4 py-4">
            <div className="space-y-2">
              <Label htmlFor="key">Key * (değiştirilemez)</Label>
              <Input
                id="key"
                placeholder="api_base_url"
                value={formData.key}
                onChange={(e) => setFormData({ ...formData, key: e.target.value })}
                disabled={!!manageDialog.config}
              />
            </div>

            <div className="space-y-2">
              <Label htmlFor="valueType">Value Type</Label>
              <Select
                value={formData.valueType}
                onValueChange={(value) =>
                  setFormData({ ...formData, valueType: value })
                }
              >
                <SelectTrigger>
                  <SelectValue />
                </SelectTrigger>
                <SelectContent>
                  <SelectItem value="string">String</SelectItem>
                  <SelectItem value="number">Number</SelectItem>
                  <SelectItem value="boolean">Boolean</SelectItem>
                  <SelectItem value="json">JSON</SelectItem>
                </SelectContent>
              </Select>
            </div>

            <div className="space-y-2">
              <Label htmlFor="value">Value *</Label>
              {formData.valueType === "json" ? (
                <Textarea
                  id="value"
                  placeholder='{"key": "value"}'
                  value={formData.value}
                  onChange={(e) =>
                    setFormData({ ...formData, value: e.target.value })
                  }
                  rows={5}
                />
              ) : (
                <Input
                  id="value"
                  placeholder="Value..."
                  value={formData.value}
                  onChange={(e) =>
                    setFormData({ ...formData, value: e.target.value })
                  }
                />
              )}
            </div>

            <div className="space-y-2">
              <Label htmlFor="description">Açıklama</Label>
              <Textarea
                id="description"
                placeholder="Bu config hakkında açıklama..."
                value={formData.description}
                onChange={(e) =>
                  setFormData({ ...formData, description: e.target.value })
                }
                rows={2}
              />
            </div>

            <div className="space-y-2">
              <Label htmlFor="platform">Platform</Label>
              <Select
                value={formData.platform}
                onValueChange={(value) =>
                  setFormData({ ...formData, platform: value })
                }
              >
                <SelectTrigger>
                  <SelectValue />
                </SelectTrigger>
                <SelectContent>
                  <SelectItem value="all">Tüm Platformlar</SelectItem>
                  <SelectItem value="ios">iOS</SelectItem>
                  <SelectItem value="android">Android</SelectItem>
                </SelectContent>
              </Select>
            </div>
          </div>

          <DialogFooter>
            <Button
              variant="outline"
              onClick={() => {
                setManageDialog({ open: false, config: null });
                resetForm();
              }}
            >
              İptal
            </Button>
            <Button
              onClick={handleSubmit}
              disabled={
                !formData.key ||
                !formData.value ||
                createMutation.isLoading ||
                updateMutation.isLoading
              }
            >
              {createMutation.isLoading || updateMutation.isLoading
                ? "İşleniyor..."
                : manageDialog.config
                ? "Güncelle"
                : "Oluştur"}
            </Button>
          </DialogFooter>
        </DialogContent>
      </Dialog>
    </AdminLayout>
  );
}

